/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { ProfessionalDetailsPageProps } from "./interfaces";
import { professionalDetailsStyle } from "./styles";
import { responsiveStyles } from "../../commons/styles";
import {
  UploadTypes,
  allowedFileTypes,
  ButtonClickType,
  inputFieldOptionalLabel,
  inputFieldMandatoryLabel,
  ToastMessages,
} from "../../constants/commons";
import { URL } from "../../API/urlEndpoints";

import { InputField } from "../../atoms/InputField";
import { UploadComp } from "../../molecules/Upload";
import useSTR from "react-str";
import { deleteData, getData, postData } from "../../utils/fetchers";
import {
  FieldRenderState,
  ISelectOption,
  ProfessionInfo,
  RegisterationFields,
} from "../../commons/interfaces";
import { toast } from "../../utils/toast";
import { RegistrationFooterButton } from "../../components/RegistrationFooterButton";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import {
  displayConfirmationWhenWindowCloses,
  useDataFromServerToDropdwon,
  getArrayOFStringFromObjArray,
  getImgURLFromBufferData,
  convertStringArraytoObjectArray,
  showDisplayHeadingAndContentFromLoadingStatus,
  getYearsFromMonths,
  getFieldConfigStatus,
  checkIfUploadIsPresentOfType,
  isFieldOptional,
  getErrorForField,
} from "../../utils/helpers";
import { useIsMobile } from "../../hooks/useIsMobile";
import { CreatableSelect } from "@curefit/pandora";
import { ShowMessage } from "../../components/ShowMessage";
import { useUserContext } from "../../hooks/cookieHooks";
import * as _ from "lodash";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import { DisplayInfo } from "../../atoms/DisplayInfo";
import { Status, WidgetType } from "../../atoms/DisplayInfo/interface";

export const ProfessionalDetailsPage: React.FunctionComponent<ProfessionalDetailsPageProps> = (
  props
) => {
  const {
    pageNavigationDetails,
    onNextClick,
    onBackButtonClick,
    onSaveAndExitClick,
    SuccessFullSaveObj,
  } = props;
  const { nextScreen } = pageNavigationDetails;
  const isMobileView = useIsMobile();
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // State checking whether user can be navigated to the next page or not
  const [
    isNextPageNavAllowed,
    setIsNextPageNavAllowed,
  ] = React.useState<boolean>(false);
  // State to maintain the profession selected
  const [selectedProfession, setSelectedProfession] = React.useState<string>();
  // State to maintain experience
  const [experience, setExperience] = React.useState<string>();
  // State to maintain Medical registration Number
  const [
    medicalRegistrationNumber,
    setMedicalRegistrationNumber,
  ] = React.useState<string>();
  // State to maintain Medical registration State
  const [
    medicalRegistrationState,
    setMedicalRegistrationState,
  ] = React.useState<string>();

  // State to maintain the bachelorsDegree
  const [bachelorsDegree, setBachelorsDegree] = React.useState<ISelectOption>();
  // State to maintain the mastersDegree
  const [mastersDegree, setMastersDegree] = React.useState<ISelectOption>();
  // State to maintain the Other Graduation Degree
  const [
    otherGraduationDegree,
    setOtherGraduationDegree,
  ] = React.useState<ISelectOption>();
  //State to maintain the uploaded docs/images
  const [successfullUploadedDocs, setSuccessfullUploadedDocs] = React.useState<
    any[]
  >([]);
  // State to maintain whether the graduation degree is uploaded or not
  const [
    isGraduationDegreeUploaded,
    setIsGraduationDegreeUploaded,
  ] = React.useState<boolean>();
  // State to maintain whether the resume is uploaded or not
  const [isResumeUploaded, setIsResumeUploaded] = React.useState<boolean>();

  const [
    isPostGraduationDegreeUploaded,
    setIsPostGraduationDegreeUploaded,
  ] = React.useState<boolean>();

  const [
    isOtherGraduationDegreeUploaded,
    setIsOtherGraduationDegreeUploaded,
  ] = React.useState<boolean>();

  // State to maintain whether the Medical Certificate is uploaded or not
  const [
    isMedicalCertificationDegreeUploaded,
    setIsMedicalCertificationDegreeUploaded,
  ] = React.useState<boolean>();

  // [GET] On page load, for fetching the details of Profession Details
  const [
    ,
    {
      result: getProfessionalDetails,
      revalidator: refetchProfessionalDetails,
      loading: getProfessionalDetailsLoading,
      error: getProfessionalDetailsError,
    },
  ] = useSTR(getData);

  // [GET] On page load, for fetching the details of Professional Details
  const [
    ,
    {
      result: degreeListFromAPI,
      revalidator: refetchDegreeList,
      loading: loadingDegreeAPI,
      error: errorDegreeAPI,
    },
  ] = useSTR(getData);

  // [POST] For Saving data on page.
  const [
    ,
    {
      result: postProfessionalDetails,
      revalidator: refetchPostProfessionalDetails,
      loading: postProfessionalDetailsLoading,
      error: postProfessionalDetailsError,
    },
  ] = useSTR(postData);

  // [DELETE] For removing the uploaded Image
  const [
    ,
    {
      result: removeUploadedAsset,
      revalidator: refetchDeleteUploadedAsset,
      loading: loadingRemovedAsset,
      error: errorRemovedAsset,
    },
  ] = useSTR(deleteData);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // On page load, calling the GET API
  React.useEffect(() => {
    refetchProfessionalDetails(
      `${URL.getProfessionalDetails(_.get(user, "empId"))}`
    );
    refetchDegreeList(`${URL.getDegreeList}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function which is called whewn the browser closes
  displayConfirmationWhenWindowCloses();

  const constructDataForPOSTAPI = React.useCallback(() => {
    let professionalDetailsObj: ProfessionInfo = {
      ...(showMedicalRegisField && {
        medicalRegistrationNumber: medicalRegistrationNumber,
      }),
      ...(showMedicalRegisStateField && {
        stateMedicalRegistration: medicalRegistrationState,
      }),
      graduationDegree:
        (bachelorsDegree &&
          Object.keys(bachelorsDegree) &&
          getArrayOFStringFromObjArray(bachelorsDegree)) ||
        [],
      otherCertifications:
        (otherGraduationDegree &&
          Object.keys(otherGraduationDegree) &&
          getArrayOFStringFromObjArray(otherGraduationDegree)) ||
        [],
      monthsOFExperience: (experience && (parseInt(experience) as number)) || 0,
      postGraduationDegree:
        (mastersDegree &&
          Object.keys(mastersDegree) &&
          getArrayOFStringFromObjArray(mastersDegree)) ||
        [],
    };
    return professionalDetailsObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProfession,
    experience,
    medicalRegistrationNumber,
    medicalRegistrationState,
    bachelorsDegree,
    otherGraduationDegree,
    mastersDegree,
    successfullUploadedDocs,
  ]);

  // For showing profession specific fields [MEDICAL_REGISTRATION_NUMBER]
  const showMedicalRegisField = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.MEDICAL_REGISTERATION_NUMBER
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field [MEDICAL_REGISTRATION_NUMBER]
  const showMedicalRegisFieldOptionalLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.MEDICAL_REGISTERATION_NUMBER
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  // For showing Relevant Details Field
  const showRelevantExperienceInputField = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.RELEVANT_EXPERIENCE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field [RELEVANT_EXPERIENCE]
  const showRelevantExperienceInputFieldOptionalLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.RELEVANT_EXPERIENCE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  // For showing profession specific fields [RESUME]
  const showResumeField = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.RESUME_UPLOAD
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field [RESUME]
  const showResumeFieldOptionalLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.RESUME_UPLOAD
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  // For showing profession specific fields [MEDICAL_REGISTERATION_STATE]
  const showMedicalRegisStateField = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.MEDICAL_REGISTERATION_STATE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field [MEDICAL_REGISTERATION_STATE]
  const showMedicalRegisStateLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.MEDICAL_REGISTERATION_STATE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  // For deciding whether to show/hide the Bachelor's Degree
  const showGraduationCertificate = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.GRADUATION_CERTIFICATE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field Bachelor's Degree
  const showGraduationCertificateLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.GRADUATION_CERTIFICATE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  // For deciding whether to show/hide the Post-Bachelor's Degree
  const showPostGraduationCertificate = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.POST_GRADUATION_CERTIFICATE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field Bachelor's Degree
  const showPostGraduationCertificateLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.POST_GRADUATION_CERTIFICATE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  // For deciding whether to show/hide the Other-Graduation Degree
  const showOtherGraduationCertificate = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getProfessionalDetails.fieldsconfig,
        RegisterationFields.OTHER_CERTIFICATION_CERTIFICATE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getProfessionalDetails]);

  // For showing the label OPTIONAL for the field Other-Graduation Degree
  const showOtherGraduationCertificateLabel = React.useMemo(() => {
    return (
      getProfessionalDetails &&
      getProfessionalDetails.fieldsconfig &&
      _.keys(getProfessionalDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getProfessionalDetails.fieldsconfig,
          RegisterationFields.OTHER_CERTIFICATION_CERTIFICATE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getProfessionalDetails]);

  React.useEffect(() => {
    if (getProfessionalDetails) {
      const {
        profession,
        monthsOFExperience,
        postGraduationDegree,
        graduationDegree,
        documents,
        medicalRegistrationNumber,
        stateMedicalRegistration,
      } = getProfessionalDetails;

      // To get the selected Documents from the server on Page load
      let documentsFromServer = getImgURLFromBufferData(documents);
      documentsFromServer && setSuccessfullUploadedDocs(documentsFromServer);
      // To set the state of MEDICAL REGISTRATION NUMBER
      medicalRegistrationNumber &&
        setMedicalRegistrationNumber(medicalRegistrationNumber);
      // To set the state of STATE MEDICAL REGISTERATION
      stateMedicalRegistration &&
        setMedicalRegistrationState(stateMedicalRegistration);
      // Profession
      profession
        ? setSelectedProfession(profession)
        : setSelectedProfession(
            _.get(user, "memberships", "") &&
              _.keys(_.get(user, "memberships", "")).length > 0 &&
              _.get(user, "memberships", "")[0].context
          );
      // Months of Experience
      monthsOFExperience && setExperience(monthsOFExperience);
      // Graduating Degree
      graduationDegree &&
        setBachelorsDegree(convertStringArraytoObjectArray(graduationDegree));
      // Post graduation degree
      postGraduationDegree &&
        setMastersDegree(convertStringArraytoObjectArray(postGraduationDegree));
    }
  }, [getProfessionalDetails, user]);

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  // Showing the success message in a toast component / Fetching data From Server
  React.useEffect(() => {
    if (postProfessionalDetails) {
      postProfessionalDetails?.message &&
        toast.success(postProfessionalDetails.message);
      if (isNextPageNavAllowed) {
        onNextClick(nextScreen);
      } else {
        SuccessFullSaveObj(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postProfessionalDetails, isNextPageNavAllowed]);

  const isSubmitAllowed = React.useMemo(() => {
    return !(
      (showResumeField ? isResumeUploaded : true) &&
      experience &&
      (showMedicalRegisField
        ? medicalRegistrationNumber && isMedicalCertificationDegreeUploaded
        : true) &&
      (showMedicalRegisStateField ? medicalRegistrationState : true) &&
      (showGraduationCertificate
        ? _.keys(bachelorsDegree).length > 0 && isGraduationDegreeUploaded
        : true)
    );
  }, [
    experience,
    isResumeUploaded,
    showResumeField,
    medicalRegistrationState,
    isMedicalCertificationDegreeUploaded,
    medicalRegistrationNumber,
    showGraduationCertificate,
    showMedicalRegisField,
    showMedicalRegisStateField,
    bachelorsDegree,
    isGraduationDegreeUploaded,
  ]);

  const onExperienceChange = (val: any) => {
    setExperience(val);
  };

  const onMedicalRegistrationNumberChange = (val: string) => {
    setMedicalRegistrationNumber(val);
  };

  const onMedicalRegistrationStateChange = (val: string) => {
    setMedicalRegistrationState(val);
  };

  // Function which is called when a graduation degree is selected
  const onBachelorsDegreeChange = (options: ISelectOption) => {
    setBachelorsDegree(options);
  };

  // Function which is called when a masters degree is selected
  const onMastersDegreeChange = (options: ISelectOption) => {
    setMastersDegree(options);
  };

  // Function which is called when a Other Graduation degree is selected
  const onOtherGraduationDegreeChange = (options: ISelectOption) => {
    setOtherGraduationDegree(options);
  };

  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    const professionalDetailsObj = constructDataForPOSTAPI();
    switch (buttonClickType) {
      case ButtonClickType.NEXT:
        if (
          !isPostGraduationDegreeUploaded &&
          mastersDegree &&
          Object.keys(mastersDegree).length > 0
        ) {
          toast.warn(ToastMessages.WARN_POST_GRADUATION_MESSAGE);
          break;
        }
        if (
          !isOtherGraduationDegreeUploaded &&
          otherGraduationDegree &&
          Object.keys(otherGraduationDegree).length > 0
        ) {
          toast.warn(ToastMessages.WARN_OTHER_GRAD_MESSAGE);
          break;
        }
        let paramsObj = {
          ...professionalDetailsObj,
          customHeader: { change_state: true },
        };
        refetchPostProfessionalDetails(
          `${URL.postProfessionalDetails(_.get(user, "empId"))}`,
          paramsObj
        );
        setIsNextPageNavAllowed(true);
        break;

      case ButtonClickType.SAVE_AND_EXIT:
        refetchPostProfessionalDetails(
          `${URL.postProfessionalDetails(_.get(user, "empId"))}`,
          professionalDetailsObj
        );
        setIsNextPageNavAllowed(false);
        break;

      default:
        break;
    }
  };

  // Hook which is called when an upload is successfully added/removed.
  // Updating the state here so that the submit button could be enabled/disabled based on the Graduation Upload
  React.useEffect(() => {
    setIsGraduationDegreeUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.GRADUATION_CERTIFICATE,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  // Updating the state here so that the submit button could be enabled/disabled based on the Resume
  React.useEffect(() => {
    setIsResumeUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.RESUME,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  React.useEffect(() => {
    setIsPostGraduationDegreeUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.POST_GRADUATION_CERTIFICATE,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  React.useEffect(() => {
    setIsOtherGraduationDegreeUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.OTHER_CERTIFICATIONS,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  // Hook which is called when an upload is successfully added/removed.
  // Updating the state here so that the submit button could be enabled/disabled based on the Medical Certificat
  React.useEffect(() => {
    setIsMedicalCertificationDegreeUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.MEDICAL_REGISTRATION_CERTIFICATE,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  // Function which is called when the user Delete's a selected Uploaded file
  const removeSelectedAsset = (removedAsset: any) => {
    const { filename, id } = removedAsset;
    refetchDeleteUploadedAsset(
      `${URL.removeUploadedAsset(_.get(user, "empId"), id, filename)}`
    );
    // Filtering and removing the DELETED Asset from the ASSET's Collection
    setSuccessfullUploadedDocs(
      successfullUploadedDocs.filter(
        (uploadedDocs) => uploadedDocs !== removedAsset
      )
    );
  };

  // Function which is executed after successfully removing the uploaded Asset
  React.useEffect(() => {
    if (removeUploadedAsset && removeUploadedAsset.success) {
      toast.success(removeUploadedAsset.message);
    }
  }, [removeUploadedAsset]);

  // Function called whenever the user successfully uploads an asset
  const onSuccessfullFileUpload = (onSuccessFileUploadObj: any) => {
    setSuccessfullUploadedDocs((oldUploadedAsset) => [
      ...oldUploadedAsset,
      onSuccessFileUploadObj,
    ]);
  };

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return (
      loadingDegreeAPI ||
      getProfessionalDetailsLoading ||
      postProfessionalDetailsLoading ||
      loadingRemovedAsset
    );
  }, [
    loadingDegreeAPI,
    getProfessionalDetailsLoading,
    postProfessionalDetailsLoading,
    loadingRemovedAsset,
  ]);

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return (
      errorRemovedAsset ||
      getProfessionalDetailsError ||
      errorDegreeAPI ||
      postProfessionalDetailsError
    );
  }, [
    errorDegreeAPI,
    getProfessionalDetailsError,
    postProfessionalDetailsError,
    errorRemovedAsset,
  ]);

  // Getting hold of Bachelor Degree Dropdown values
  const bachelorDegreeDropdwonValues = useDataFromServerToDropdwon(
    degreeListFromAPI?.bachelorsDegrees
  );

  // Getting hold of Masters Degree Dropdown values
  const mastersDegreeDropdwonValues = useDataFromServerToDropdwon(
    degreeListFromAPI?.mastersDegrees
  );

  // Getting hold of Other Grsduation Degree Dropdown values
  const otherGraduationDegreeDropdwonValues = useDataFromServerToDropdwon(
    degreeListFromAPI?.otherDegrees
  );

  return (
    <span css={[responsiveStyles, professionalDetailsStyle]}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: loadingDegreeAPI || getProfessionalDetailsLoading,
            postLoginScreen:
              postProfessionalDetailsLoading || loadingRemovedAsset,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: loadingDegreeAPI || getProfessionalDetailsLoading,
            postLoginScreen:
              postProfessionalDetailsLoading || loadingRemovedAsset,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        <h2>Professional Details</h2>

        <div>
          <div className="mb-3">
            <span className="profession-label">{selectedProfession}</span>
          </div>
          {showRelevantExperienceInputField && (
            <div>
              <div className="">
                <InputField
                  placeholder={`Months of relevant Experience ${
                    showRelevantExperienceInputFieldOptionalLabel
                      ? inputFieldOptionalLabel
                      : inputFieldMandatoryLabel
                  }`}
                  type="number"
                  className="my-3"
                  initialValue={experience}
                  onChange={onExperienceChange}
                />
              </div>
              <div>
                {experience && (
                  <div className="experience-style">
                    {getYearsFromMonths(experience) + ` years of Experience`}
                  </div>
                )}
                <div className="error-text">
                  {getProfessionalDetails &&
                    getProfessionalDetails.errors &&
                    getErrorForField(
                      RegisterationFields.RELEVANT_EXPERIENCE,
                      getProfessionalDetails.errors
                    )}
                </div>
              </div>
            </div>
          )}

          {showMedicalRegisField && (
            <div className="">
              <InputField
                placeholder={`State Medical Registration Number / MCI ${
                  showMedicalRegisFieldOptionalLabel
                    ? inputFieldOptionalLabel
                    : inputFieldMandatoryLabel
                }`}
                type="text"
                className="my-3"
                initialValue={medicalRegistrationNumber}
                onChange={onMedicalRegistrationNumberChange}
                validationErrMsg={
                  getProfessionalDetails &&
                  getProfessionalDetails.errors &&
                  getErrorForField(
                    RegisterationFields.MEDICAL_REGISTERATION_NUMBER,
                    getProfessionalDetails.errors
                  )
                }
              />
            </div>
          )}

          {showMedicalRegisStateField && (
            <div className="">
              <InputField
                className="my-3"
                placeholder={`State of Medical Registration ${
                  showMedicalRegisStateLabel
                    ? inputFieldOptionalLabel
                    : inputFieldMandatoryLabel
                }`}
                type="text"
                validationErrMsg={
                  getProfessionalDetails &&
                  getProfessionalDetails.errors &&
                  getErrorForField(
                    RegisterationFields.MEDICAL_REGISTERATION_STATE,
                    getProfessionalDetails.errors
                  )
                }
                initialValue={medicalRegistrationState}
                onChange={onMedicalRegistrationStateChange}
              />
            </div>
          )}

          {showMedicalRegisField && (
            <div className="resposnive-container-margin">
              <div className="d-flex">
                <h6>Certificate of Medical Registration</h6>
                <span className="small-text ml-2">
                  {showMedicalRegisFieldOptionalLabel}
                </span>
              </div>
              <div className="short-description-style mb-1 mt-1">
                Add all the pages of the certificate
              </div>
              {getProfessionalDetails &&
                getProfessionalDetails.errors &&
                !!getErrorForField(
                  RegisterationFields.MEDICAL_REGISTERATION_UPLOAD,
                  getProfessionalDetails.errors
                )?.length && (
                  <DisplayInfo
                    title={[
                      getErrorForField(
                        RegisterationFields.MEDICAL_REGISTERATION_UPLOAD,
                        getProfessionalDetails.errors
                      ),
                    ]}
                    widgetType={WidgetType.INFO}
                    status={Status.warning}
                  />
                )}
              <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
                <UploadComp
                  onSuccess={onSuccessfullFileUpload}
                  uploadType={UploadTypes.MEDICAL_REGISTRATION_CERTIFICATE}
                  removeSelectedAsset={removeSelectedAsset}
                  uploadedFiles={successfullUploadedDocs}
                  allowedFilesType={[
                    allowedFileTypes.jpeg,
                    allowedFileTypes.jpg,
                    allowedFileTypes.png,
                    allowedFileTypes.pdf,
                  ]}
                  allowedFileSize={10}
                />
              </div>
            </div>
          )}

          {showResumeField && (
            <div className="resposnive-container-margin">
              <div className="d-flex">
                <h6>Resume</h6>
                <span className="small-text ml-2">
                  {showResumeFieldOptionalLabel ? (
                    showResumeFieldOptionalLabel
                  ) : (
                    <div className="responsive-error-asterix-style">
                      {inputFieldMandatoryLabel}
                    </div>
                  )}
                </span>
              </div>
              <div className="short-description-style mb-1 mt-1">
                Add all the pages of the resume
              </div>
              {getProfessionalDetails &&
                getProfessionalDetails.errors &&
                !!getErrorForField(
                  RegisterationFields.RESUME_UPLOAD,
                  getProfessionalDetails.errors
                )?.length && (
                  <DisplayInfo
                    title={[
                      getErrorForField(
                        RegisterationFields.RESUME_UPLOAD,
                        getProfessionalDetails.errors
                      ),
                    ]}
                    widgetType={WidgetType.INFO}
                    status={Status.warning}
                  />
                )}
              <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
                <UploadComp
                  onSuccess={onSuccessfullFileUpload}
                  uploadType={UploadTypes.RESUME}
                  removeSelectedAsset={removeSelectedAsset}
                  uploadedFiles={successfullUploadedDocs}
                  allowedFilesType={[
                    allowedFileTypes.jpeg,
                    allowedFileTypes.jpg,
                    allowedFileTypes.png,
                    allowedFileTypes.pdf,
                  ]}
                  allowedFileSize={10}
                />
              </div>
            </div>
          )}

          {showGraduationCertificate && (
            <div className="resposnive-container-margin">
              <div className="d-flex">
                <h6>Graduation Certificate</h6>
                <span className="small-text ml-2">
                  {showGraduationCertificateLabel ? (
                    showGraduationCertificateLabel
                  ) : (
                    <div className="responsive-error-asterix-style">
                      {inputFieldMandatoryLabel}
                    </div>
                  )}
                </span>
              </div>

              <div className="my-2">
                {bachelorsDegree && Object.keys(bachelorsDegree).length > 0 && (
                  <div className="my-2">
                    <div className="sub-heading-dropdown">
                      Select or Enter your bachelor's Degrees
                    </div>
                  </div>
                )}
              </div>

              <div className="option-field-margin-class">
                <CreatableSelect
                  options={bachelorDegreeDropdwonValues}
                  value={bachelorsDegree}
                  onChange={onBachelorsDegreeChange}
                  required
                  placeholder="Select or Enter your bachelor's Degrees"
                  showIndicator
                  isMulti
                  className="w-100"
                  formatCreateLabel={(inputValue: string) =>
                    `Create "${inputValue}"`
                  }
                />
              </div>
              {getProfessionalDetails &&
                getProfessionalDetails.errors &&
                !!getErrorForField(
                  RegisterationFields.GRADUATION_CERTIFICATE,
                  getProfessionalDetails.errors
                )?.length && (
                  <DisplayInfo
                    title={[
                      getErrorForField(
                        RegisterationFields.GRADUATION_CERTIFICATE,
                        getProfessionalDetails.errors
                      ),
                    ]}
                    widgetType={WidgetType.INFO}
                    status={Status.warning}
                  />
                )}
              <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
                <UploadComp
                  uploadType={UploadTypes.GRADUATION_CERTIFICATE}
                  onSuccess={onSuccessfullFileUpload}
                  uploadedFiles={successfullUploadedDocs}
                  removeSelectedAsset={removeSelectedAsset}
                  allowedFilesType={[
                    allowedFileTypes.jpeg,
                    allowedFileTypes.jpg,
                    allowedFileTypes.png,
                    allowedFileTypes.pdf,
                  ]}
                  allowedFileSize={10}
                />
              </div>
            </div>
          )}

          {showPostGraduationCertificate && (
            <div className="resposnive-container-margin">
              <div className="d-flex">
                <h6>Post Graduation Certificate</h6>
                <span className="small-text ml-2">
                  {showPostGraduationCertificateLabel ? (
                    showPostGraduationCertificateLabel
                  ) : (
                    <div className="responsive-error-asterix-style">
                      {inputFieldMandatoryLabel}
                    </div>
                  )}
                </span>
              </div>

              {mastersDegree && Object.keys(mastersDegree).length > 0 && (
                <div className="my-2">
                  <div className="sub-heading-dropdown">
                    Select or Enter your Master's Degrees
                  </div>
                </div>
              )}

              <div className="option-field-margin-class">
                <CreatableSelect
                  options={mastersDegreeDropdwonValues}
                  value={mastersDegree}
                  onChange={onMastersDegreeChange}
                  required
                  placeholder="Select or Enter your Master's Degrees"
                  showIndicator
                  isMulti
                  className="w-100"
                  formatCreateLabel={(inputValue: string) =>
                    `Create "${inputValue}"`
                  }
                />
              </div>
              {getProfessionalDetails &&
                getProfessionalDetails.errors &&
                !!getErrorForField(
                  RegisterationFields.POST_GRADUATION_CERTIFICATE,
                  getProfessionalDetails.errors
                )?.length && (
                  <DisplayInfo
                    title={[
                      getErrorForField(
                        RegisterationFields.POST_GRADUATION_CERTIFICATE,
                        getProfessionalDetails.errors
                      ),
                    ]}
                    widgetType={WidgetType.INFO}
                    status={Status.warning}
                  />
                )}
              <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
                <UploadComp
                  uploadType={UploadTypes.POST_GRADUATION_CERTIFICATE}
                  onSuccess={onSuccessfullFileUpload}
                  uploadedFiles={successfullUploadedDocs}
                  removeSelectedAsset={removeSelectedAsset}
                  allowedFilesType={[
                    allowedFileTypes.jpeg,
                    allowedFileTypes.jpg,
                    allowedFileTypes.png,
                    allowedFileTypes.pdf,
                  ]}
                  allowedFileSize={10}
                />
              </div>
            </div>
          )}
        </div>
        {showOtherGraduationCertificate && (
          <div className="resposnive-container-margin">
            <div className="d-flex">
              <h6>Other certificates</h6>
              <span className="small-text ml-2">
                {showOtherGraduationCertificateLabel ? (
                  showOtherGraduationCertificateLabel
                ) : (
                  <div className="responsive-error-asterix-style">
                    {inputFieldMandatoryLabel}
                  </div>
                )}
              </span>
            </div>

            {mastersDegree && Object.keys(mastersDegree).length > 0 && (
              <div className="my-2">
                <div className="sub-heading-dropdown">
                  Select or Enter other certificates
                </div>
              </div>
            )}

            <div className="option-field-margin-class">
              <CreatableSelect
                options={otherGraduationDegreeDropdwonValues}
                value={otherGraduationDegree}
                onChange={onOtherGraduationDegreeChange}
                required
                placeholder="Select or Enter other certificates"
                showIndicator
                isMulti
                className="w-100"
                formatCreateLabel={(inputValue: string) =>
                  `Create "${inputValue}"`
                }
              />
            </div>
            {getProfessionalDetails &&
              getProfessionalDetails.errors &&
              !!getErrorForField(
                RegisterationFields.OTHER_CERTIFICATION_CERTIFICATE,
                getProfessionalDetails.errors
              )?.length && (
                <DisplayInfo
                  title={[
                    getErrorForField(
                      RegisterationFields.OTHER_CERTIFICATION_CERTIFICATE,
                      getProfessionalDetails.errors
                    ),
                  ]}
                  widgetType={WidgetType.INFO}
                  status={Status.warning}
                />
              )}
            <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
              <UploadComp
                uploadType={UploadTypes.OTHER_CERTIFICATIONS}
                onSuccess={onSuccessfullFileUpload}
                uploadedFiles={successfullUploadedDocs}
                removeSelectedAsset={removeSelectedAsset}
                allowedFilesType={[
                  allowedFileTypes.jpeg,
                  allowedFileTypes.jpg,
                  allowedFileTypes.png,
                  allowedFileTypes.pdf,
                ]}
                allowedFileSize={10}
              />
            </div>
          </div>
        )}
        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
      </RegistrationLayout>

      <RegistrationFooterButton
        disableNextButton={isSubmitAllowed}
        onPositiveActionClick={onPositiveActionClick}
        onBackButtonClick={onBackButtonClick}
      />
    </span>
  );
};
